import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { RadioGroupField } from 'components/form';
import { Stack, Typography } from '@mui/material';
import { useSelector, shallowEqual } from 'react-redux';
import { useFormContext, useWatch } from 'react-hook-form';
import { ProposalSaleType } from '@trnsact/trnsact-shared-types';
import { FormSectionHeader } from 'components/shared/FormSectionHeader';
import { portalConfigurationsSelectors } from 'redux/portalConfigurationReducer';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import {
  DeskingPanel,
  FormSections,
  deskingSelectors,
  deskingConfigByAccId,
  FinanceProgramFormValues,
  collectVariablesForCreateProposal,
} from 'modules/desking';

import { dealBuilderApi } from '../../../api';
import { SALE_TYPE_OPTIONS } from '../../../constants';
import { useDealBuilderContext } from '../../../context';
import { FinanceMenuDisabledState } from './FinanceMenuDisabledState';
import { DealBuilderFormValues, DealBuilderIds, StepComponentProps } from '../../../types';

export const DealBuilderDesking = ({ navigationNode }: StepComponentProps) => {
  const [updateProposal, { loading, error }] = useMutation(dealBuilderApi.updateProposal);

  const account = useSelector((state: any) => state.account);
  const userProfile = useSelector((state: any) => state.userProfile);

  const aftermarketPortalConfigurations = useSelector(portalConfigurationsSelectors.getAftermarket);
  const dealBuilderDeskingData = useSelector(deskingSelectors.dealBuilderDeskingData, shallowEqual);

  const {
    activeEntity,
    setActiveEntity,
    equipment: { selectedEquipmentItem },
    sourceData: { vo, creditApplication },
  } = useDealBuilderContext();

  const [deskingChange, setDeskingChange] = useState<FinanceProgramFormValues | null>(null);

  const { control, setValue } = useFormContext<DealBuilderFormValues>();
  const saleType = useWatch({ control, name: 'financeMenu.saleType' });
  const isEnabled = useWatch({ control, name: 'financeMenu.isEnabled' });

  const [hasSavedDeskingData] = useState(() => !!activeEntity?.proposal?.settings?.configuration?.pricing);

  const saveDisabledState = async (proposalId: string) => {
    await updateProposal({
      variables: {
        id: proposalId,
        fieldsObj: {
          settings: null,
          saleType: ProposalSaleType.FinanceOnly,
        },
      },
      onCompleted: () =>
        setActiveEntity(prev => {
          if (!prev) return prev;

          return {
            ...prev,
            proposal: { ...prev.proposal, settings: null, saleType: ProposalSaleType.FinanceOnly },
          };
        }),
    });
  };

  const saveDeskingStep = async ({ proposalId, voId }: DealBuilderIds) => {
    if (!isEnabled) return saveDisabledState(proposalId);

    if (!deskingChange || (hasSavedDeskingData && !dealBuilderDeskingData.isProposalDataLoaded)) return;

    const { input } = collectVariablesForCreateProposal({
      voId,
      isDraft: true,
      formData: deskingChange,
      pricing: dealBuilderDeskingData.pricing,
      menuByTerms: dealBuilderDeskingData.builder,
      productsConfigInMenu: dealBuilderDeskingData.productsConfigInMenu,
      commonProductsConfiguration: dealBuilderDeskingData.commonProductsConfiguration,
    });

    await updateProposal({
      variables: {
        id: proposalId,
        fieldsObj: {
          saleType: input.saleType,
          settings: input.settings,
        },
      },
      onCompleted: () =>
        setActiveEntity(prev => {
          if (!prev) return prev;

          return {
            ...prev,
            proposal: { ...prev.proposal, settings: input.settings },
          };
        }),
    });
  };

  return (
    <Stack gap={1}>
      <RenderWithCondition
        condition={isEnabled}
        fallBack={<FinanceMenuDisabledState onEnable={() => setValue('financeMenu.isEnabled', true)} />}
      >
        <Stack gap={1}>
          <Stack gap={1}>
            <FormSectionHeader name="Sale Type" value="saleType" />
            <RadioGroupField
              row
              control={control}
              name="financeMenu.saleType"
              options={SALE_TYPE_OPTIONS.map(({ value, label, description }) => ({
                value,
                label: (
                  <Stack direction="row" gap={0.5} alignItems="center">
                    <Typography variant="body2" fontWeight={500}>
                      {label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {description}
                    </Typography>
                  </Stack>
                ),
              }))}
            />
          </Stack>

          <DeskingPanel
            vo={vo}
            accountId={account.id}
            isProposalLoading={false}
            externalSaleType={saleType}
            userEmail={userProfile.email}
            onSubmitDesking={async () => {}}
            creditApplication={creditApplication}
            onCreateProposalProduct={async () => null}
            configurationByAccount={deskingConfigByAccId[account.id]}
            externalEquipmentId={selectedEquipmentItem?.equipment?.id ?? null}
            proposalId={hasSavedDeskingData ? (activeEntity?.proposal.proposalId ?? null) : null}
            isProductCreationAvailable={aftermarketPortalConfigurations?.allowCreateCustomProducts}
            onFormValuesChanged={formValues => setDeskingChange(formValues)}
            panelFormCustomActions={{
              isHidden: true,
            }}
            customSections={{
              [FormSections.Contact]: { isHidden: true },
              [FormSections.SaleDetails]: { isHidden: true },
              [FormSections.Equipment]: { isHidden: true },
              [FormSections.SendVia]: { isHidden: true },
              [FormSections.NotesToBuyer]: { isHidden: true },
            }}
          />
        </Stack>
      </RenderWithCondition>
      {navigationNode({
        error,
        isDisabled: false,
        isLoading: loading,
        fn: saveDeskingStep,
      })}
    </Stack>
  );
};
